import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import { concat } from '@ember/helper';
import style from 'ember-style-modifier/modifiers/style';
import { PhArrowRight } from 'ember-phosphor-icons';
import type { BetaProgressCard } from 'tio-employee/utils/beta/home-model';

/**
 * One "Active Benefits" card: header row, then an inset panel holding the big
 * figure, a 12px progress track, and a footnote.
 *
 * `percent` is clamped to 0–100 in the view model, so the fill width cannot go
 * out of range.
 */
const BetaHomeProgressCard: TOC<{
  Args: { card: BetaProgressCard };
  Element: HTMLDivElement;
}> = <template>
  <div class="tio-beta-home__progress-card" data-test-beta-progress={{@card.id}} ...attributes>
    <div class="tio-beta-home__progress-head">
      <div class="tio-beta-home__progress-title">
        {{#let @card.icon as |Icon|}}
          <Icon @size={{16}} />
        {{/let}}
        <span>{{@card.title}}</span>
      </div>

      <LinkTo @route={{@card.route}} class="tio-beta-home__link tio-beta-home__link--lg">
        <span>{{@card.linkLabel}}</span>
        <PhArrowRight @size={{20}} />
      </LinkTo>
    </div>

    <div class="tio-beta-home__progress-body">
      <div class="tio-col-tight">
        <div class="tio-beta-home__progress-value">{{@card.value}}</div>
        <div class="tio-beta-home__progress-caption">{{@card.caption}}</div>
      </div>

      <div
        class="tio-beta-home__track"
        role="progressbar"
        aria-valuenow={{@card.percent}}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-label="{{@card.title}} — {{@card.caption}}"
      >
        <div class="tio-beta-home__track-fill" {{style width=(concat @card.percent "%")}}></div>
      </div>

      <div class="tio-beta-home__meta">
        <span>{{@card.meta}}</span>
        {{#let @card.metaIcon as |MetaIcon|}}
          <MetaIcon @size={{16}} />
        {{/let}}
      </div>
    </div>
  </div>
</template>;

export default BetaHomeProgressCard;
